import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { LinkIcon } from "../iconography";
import { getShipsTogetherBadgeStyle } from "../../../utils/trackManagementPalette";
import {
  fetchShipWithMembersCached,
  shipWithCacheKey,
} from "../../../utils/shipWith";

const useStyles = makeStyles((theme) => ({
  pill: {
    ...getShipsTogetherBadgeStyle(theme),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    fontWeight: 700,
    letterSpacing: "0.02em",
    lineHeight: 1,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    boxSizing: "border-box",
  },
  // Same silhouette as BetaBadge: 18px tall in list rows.
  small: {
    height: 18,
    fontSize: "0.6875rem",
    padding: theme.spacing(0, 0.75),
    "& $icon": { fontSize: "0.875rem" },
  },
  medium: {
    height: 22,
    fontSize: "0.75rem",
    padding: theme.spacing(0, 1),
    "& $icon": { fontSize: "1rem" },
  },
  // Mirrors the calendar's eventStatusBadge dimensions so a card's title row
  // keeps its height; the calendar passes its own className to size further.
  calendar: {
    height: "auto",
    flexShrink: 0,
    marginLeft: theme.spacing(0.75),
    padding: theme.spacing(0.125, 0.75),
    fontSize: "0.6rem",
    lineHeight: 1.5,
    "& $icon": { fontSize: "0.75rem" },
  },
  compact: {
    padding: theme.spacing(0, 0.25),
  },
  icon: {
    flexShrink: 0,
  },
  count: {
    marginLeft: 2,
  },
  tooltipHeading: {
    display: "block",
    fontWeight: 600,
  },
  tooltipLine: {
    display: "block",
  },
}));

const uniqueIds = (ids) =>
  Array.from(new Set((Array.isArray(ids) ? ids : []).filter(Boolean)));

/** "PO 123 · Acme Lumber" — one tooltip line per sibling. */
const memberLine = (member) => {
  const consignee = member?.shipTo?.name;
  return consignee ? `PO ${member.poNumber} · ${consignee}` : `PO ${member.poNumber}`;
};

/**
 * The one glyph for a linked load (AD-12): link icon + member count in a
 * deepPurple pill, rendered wherever a load or a check-in can appear.
 *
 * Reads `ids` when given, else `record.shipWithIds`, else a check-in's
 * `record.outboundIds`; renders nothing below two ids. The tooltip lists the
 * sibling POs, fetched lazily on open through the shared 60 s cache, so a list
 * of linked rows costs nothing until hovered. `selfId` (default `record.id`)
 * is never listed. Until the fetch resolves, and if it fails, the tooltip is
 * the count line only.
 */
export default function ShipsTogetherBadge({
  record,
  ids,
  size = "small",
  compact = false,
  variant = "pill",
  withTooltip = true,
  selfId,
  className,
  ...rest
}) {
  const classes = useStyles();
  const idList = uniqueIds(ids ?? record?.shipWithIds ?? record?.outboundIds);
  const key = shipWithCacheKey(idList);
  const self = selfId ?? record?.id;

  const [members, setMembers] = useState(null);
  const requestedKey = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A different group means whatever was fetched no longer applies.
  useEffect(() => {
    if (requestedKey.current !== key) {
      requestedKey.current = null;
      setMembers(null);
    }
  }, [key]);

  const loadMembers = useCallback(() => {
    if (!key || requestedKey.current === key) return;
    requestedKey.current = key;
    fetchShipWithMembersCached(idList).then((loaded) => {
      if (!mounted.current || requestedKey.current !== key) return;
      // A failed member has no PO to show; if none loaded, stay count-only and
      // let the next open try again.
      const usable = loaded.filter((m) => !m?.unavailable && m?.poNumber);
      if (usable.length) setMembers(usable);
      else requestedKey.current = null;
    });
    // idList is derived from key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (idList.length < 2) return null;

  const count = idList.length;
  const label = `Ships together — ${count} loads on one truck`;

  const pill = (
    <span
      role="img"
      aria-label={label}
      className={clsx(
        classes.pill,
        variant === "calendar" ? classes.calendar : classes[size],
        compact && classes.compact,
        className,
      )}
      {...rest}
    >
      <LinkIcon className={classes.icon} aria-hidden />
      {!compact && <span className={classes.count}>{count}</span>}
    </span>
  );

  if (!withTooltip) return pill;

  const siblings = (members || []).filter((m) => m.id !== self);
  const title = (
    <span>
      <span className={classes.tooltipHeading}>{label}</span>
      {siblings.map((m) => (
        <span key={m.id} className={classes.tooltipLine}>
          {memberLine(m)}
        </span>
      ))}
    </span>
  );

  return (
    <Tooltip title={title} interactive enterDelay={350} onOpen={loadMembers}>
      {pill}
    </Tooltip>
  );
}
